import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import EditFacilitiesPopup from '../EditFacilitiesPopup';
import { fetchClubLocationsWithFacilities } from '../../../../actions/club-location-actions';
import config from '../../../../../config';
import { LocationTypeIds } from '../../../../utils/constants';
import Img from '../../../Img';

import styles from '../eventCreate.scss';

const FacilityLocations = (props) => {
  const {
    clubId,
    formApi,
    eventFacilities,
  } = props;

  const clubLocation = useSelector(({ clubLocation }) => clubLocation);
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
  const [showEditFacilitiesPopup, setShowEdiFaciltiiesPopup] = useState(false);
  const [openLocationIds, setOpenLocationIds] = useState(new Set());
  const [visibleLocations, setVisibleLocations] = useState([]);
  const dispatch = useDispatch();

  const { locationsWithFacilities } = clubLocation;

  const getClubLocations = async () => {
    dispatch(fetchClubLocationsWithFacilities(clubId));
  };

  useEffect(() => {
    if (!locationsWithFacilities?.length) {
      getClubLocations();
    }
  }, []);

  const selectedFacilityIds = React.useMemo(() => {
    if (!eventFacilities?.length) {
        return new Set();
    }

    return new Set(
      eventFacilities.map((f) => f.clubLocationFacilityId),
    );
  }, [eventFacilities]);

  useEffect(() => {
    if (!locationsWithFacilities?.length || selectedFacilityIds.size === 0) {
      setVisibleLocations([]);
      return;
    }

    const filtered = locationsWithFacilities
      .map((location) => {
        const selectedFacilities = location.facilities.filter((facility) => selectedFacilityIds.has(facility.id));

        if (selectedFacilities.length === 0) {
            return null;
        }

        return {
          ...location,
          facilities: selectedFacilities,
        };
      })
      .filter(Boolean);

    setVisibleLocations(filtered);
  }, [locationsWithFacilities, selectedFacilityIds]);

  const toggleLocation = (locationId) => {
    setOpenLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };

  const isLocationOpen = (locationId) => openLocationIds.has(locationId);

  const handleAddFacilitiesPopupConfirm = (facilities) => {
    const nextEventFacilities = facilities.map((f) => ({
      clubLocationFacilityId: f.id,
    }));

    formApi.setValue('eventFacilities', nextEventFacilities);
    setShowEdiFaciltiiesPopup(false);
  };

  const addFacilities = () => {
    setShowEdiFaciltiiesPopup(true);
  };

  const getClubLocationSettingsAnchor = () => {
    const explainerPrefix = 'Additional locations and facilities can be added to your Club in your';
    const clubLocationLinkText = 'Club Settings';
    const anchorLink = `${config.appHost}/clubs/${clubId}/locations`;

    return (
      <>
        {explainerPrefix}&nbsp;<a href={anchorLink} target="_blank" rel="noopener noreferrer" data-testid="event-edit.facilities.club-settings-link">{clubLocationLinkText}</a>
      </>
    );
  };

  return (
    <div className="col-12">
      <div
        className={`form-block-divider ${isDropdownExpanded ? 'is-open' : 'mb16'} clickable`}
        onClick={() => {
          setIsDropdownExpanded(!isDropdownExpanded);
        }}
        data-testid="event-edit.facilities.toggle"
      >
        <div className="title">
          <div><label>Event Facilities</label></div>
          <small style={{ textTransform: 'unset' }}>Select which facilities you will use for the event</small>
        </div>
        <div className="click-area click-area-32">
          <i className={`uts uts-${isDropdownExpanded ? 'collapse' : 'expand'}-arrow`} />
        </div>
      </div>
      {isDropdownExpanded && (
        <div className="row">
          <div className="col-12">
            <div className={styles.facilitesContainer}>
              {!visibleLocations?.length ? (
                <p className={styles.addWrapper}>
                  <div className={'text-bold mb8'}>No facilities have been added to your club yet.</div>
                  <div>Please click &quot;Club Settings&quot; below to add facilities. Once added, return here and refresh this section to select a facility for your event.</div>
                  <div
                    className={`cursor-pointer text-teal mt8`}
                    onClick={getClubLocations}
                    data-testid="event-edit.facilities.refresh-locations-btn"
                  >
                    <Img src="/img/icons/refresh.svg" alt="Refresh Icon" />&nbsp;Refresh
                  </div>
                </p>
              ) : (
                <>
                  {visibleLocations?.map((location) => (
                    <section className={styles.locationWrapper} key={location.id}>
                      <div
                        className={styles.locationHeader}
                        onClick={() => toggleLocation(location.id)}
                        data-testid={`event-edit.facilities.location-${location.id}-toggle`}
                      >
                        <label className={styles.locationName}>
                          {location.name} {location.locationTypeId === LocationTypeIds.PRIMARY ? '(Primary)' : ''}
                        </label>
                        <div className="click-area click-area-32">
                          <i
                            className={`uts uts-${isLocationOpen(location.id) ? 'collapse' : 'expand'}-arrow`}
                          />
                        </div>
                      </div>
                      {isLocationOpen(location.id) && (
                        <div className={styles.facilitiesWrapper}>
                          {location.facilities.map((facility) => (
                            <label className={styles.facilityName} key={facility.id}>
                              {facility.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </section>
                  ))}
                  <p className={styles.addWrapper}>
                    <span
                      className="cursor-pointer text-teal"
                      onClick={addFacilities}
                      data-testid="event-edit.facilities.add-facilities-link"
                    >
                      Click here&nbsp;
                    </span>
                    to add or change locations and facilities for this event.
                  </p>
                </>
              )}
            </div>
            <div className={`${styles.clubLocationSettings} mb16`}>
              {getClubLocationSettingsAnchor()}
            </div>

          </div>
        </div>
      )}
      {showEditFacilitiesPopup && (
        <EditFacilitiesPopup
          closePopup={() => {
            setShowEdiFaciltiiesPopup(false);
          }}
          clubLocations={locationsWithFacilities}
          selectedFacilityIds={selectedFacilityIds}
          confirmPopup={handleAddFacilitiesPopupConfirm}
          getClubLocations={getClubLocations}
        />
      )}
    </div>
  );
};

export default FacilityLocations;
